import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, DatePicker, Button, message } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { DESTINATION_COUNTRIES } from "../../../utils/constantData";
import { actions } from "../../../pages/authenticatedApp/backoffice/OrderDetails/slice";
import moment from "moment";

const { TextArea } = Input;
const { Option } = Select;

const EditOrderDetails = ({ orderDetails, visible, onClose }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [isEditing, setIsEditing] = useState(false);
  const [sampleVariant, setSampleVariant] = useState(
    orderDetails?.sampleVariant || ""
  );
  const [samplingType, setSamplingType] = useState(
    orderDetails?.samplingType || ""
  );

  const packages = useSelector((state) => state.orderDetails?.packages || []);

  const fieldsBaseOnConfig =
    orderDetails?.projectCodeId?.projectConfig?.metadataFields || {};

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsEditing(true);
      const data = {
        _id: orderDetails?._id,
        sampleVariant: values.variant,
        sampleType: values.sampleType,
        destinationCountry: values.destinationCountry,
        samplingType: values.samplingType,
        codewiseReason: values.codewiseReason,
        expectedConsignmentReadiness:
          values.expectedConsignmentReadiness?.toISOString(),
        expectedShipmentDate: values.expectedShipmentDate?.toISOString(),
        batchId: values.batchId,
        supplier: values.supplier,
        sector: values.sector,
        quantity: values.quantity,
        quantityUnit: values.quantityUnit,
        packageId: values.package,
        siteAddress: values.siteAddress,
        additionalNotes: values.notes,
      };
      dispatch(
        actions.updateOrderDetails({
          data,
          onSuccess: () => {
            message.success("Order details updated successfully");
            setIsEditing(false);
            onClose();
          },
          onFailure: (err) => {
            message.error(err?.message || "Failed to update order details");
            setIsEditing(false);
          },
        })
      );
    } catch (error) {
      console.error("Form validation failed:", error);
      message.error("Please check all required fields");
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleValuesChange = (changedValues) => {
    // Update sample variant and clear package when variant changes
    if (changedValues.variant !== undefined) {
      setSampleVariant(changedValues.variant);
      form.setFieldsValue({ package: "" });
    }
    
    // Update sampling type and clear codewise reason when not Code-wise Sampling
    if (changedValues.samplingType !== undefined) {
      setSamplingType(changedValues.samplingType);
      if (changedValues.samplingType !== "Code-wise Sampling") {
        form.setFieldsValue({ codewiseReason: "" });
      }
    }
  };

  const renderFormField = ({
    fieldKey,
    label,
    type = "input",
    options = [],
    span = 1,
  }) => {
    let isRequired = fieldsBaseOnConfig?.[fieldKey]?.required;
    let isMandatory = fieldsBaseOnConfig?.[fieldKey]?.mandatory;
    
    // Special case: codewiseReason should be visible and mandatory when Code-wise Sampling is selected
    if(fieldKey === "codewiseReason" && samplingType === "Code-wise Sampling") {
      isRequired = true;
      isMandatory = true;
    }

    if (!isRequired) {
      return null;
    }

    const commonProps = {
      key: fieldKey,
      name: fieldKey,
      label,
      rules: isMandatory
        ? [{ required: true, message: `Please enter ${label.toLowerCase()}` }]
        : [],
    };

    switch (type) {
      case "select":
        return (
          <Form.Item {...commonProps}>
            <Select placeholder={`Select ${label.toLowerCase()}`}>
              {options.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}{option.isoCode && ` (${option.isoCode})`}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );

      case "date":
        return (
          <Form.Item {...commonProps}>
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              placeholder={`Select ${label.toLowerCase()}`}
            />
          </Form.Item>
        );

      case "textarea":
        return (
          <Form.Item {...commonProps} style={{ gridColumn: `span ${span}` }}>
            <TextArea
              rows={1}
              placeholder={`Enter ${label.toLowerCase()}`}
              showCount
            />
          </Form.Item>
        );

      default:
        return (
          <Form.Item {...commonProps}>
            <Input
              placeholder={`Enter ${label.toLowerCase()}`}
              type={fieldKey === "quantity" ? "number" : "text"}
            />
          </Form.Item>
        );
    }
  };

  useEffect(() => {
    // Pre-populate form with existing order data
    form.setFieldsValue({
      variant: orderDetails?.sampleVariant || "",
      sampleType: orderDetails?.sampleType || "",
      destinationCountry: orderDetails?.destinationCountry || "",
      samplingType: orderDetails?.samplingType || "",
      codewiseReason: orderDetails?.codewiseReason || "",
      expectedConsignmentReadiness: orderDetails?.expectedConsignmentReadiness
        ? moment(orderDetails.expectedConsignmentReadiness)
        : null,
      expectedShipmentDate: orderDetails?.expectedShipmentDate
        ? moment(orderDetails.expectedShipmentDate)
        : null,
      batchId: orderDetails?.batchId || "",
      supplier: orderDetails?.supplier || "",
      sector: orderDetails?.sector || "",
      quantity: orderDetails?.quantity || "",
      quantityUnit: orderDetails?.quantityUnit || "",
      package: orderDetails?.packages[0]?._id || "",
      siteAddress: orderDetails?.siteAddress || "",
      notes: orderDetails?.additionalNotes || "",
    });
  }, [orderDetails, form]);

  const companyId = orderDetails?.company?._id;
  const commodityId = orderDetails?.commodityId;

  useEffect(() => {
    if (visible && companyId && commodityId && sampleVariant) {
      dispatch(
        actions.fetchPackages({
          companyId: companyId,
          commodityId: commodityId,
          sampleVariant: sampleVariant,
        })
      );
    }
  }, [visible, companyId, commodityId, sampleVariant, dispatch]);

  return (
    <Modal
      title="Edit Order Details"
      visible={visible}
      onCancel={handleCancel}
      width={800}
      style={{ top: 40 }}
      bodyStyle={{
        maxHeight: "75vh",
        overflowY: "auto",
        padding: "20px",
      }}
      footer={[
        <Button key="cancel" onClick={handleCancel} icon={<CloseOutlined />}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          icon={<SaveOutlined />}
          loading={isEditing}
        >
          {isEditing ? "Saving..." : "Save Changes"}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        onValuesChange={handleValuesChange}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            rowGap: "0px",
            columnGap: "16px",
          }}
        >
          {/* Variant */}
          {fieldsBaseOnConfig?.variant?.required &&
            renderFormField({
              fieldKey: "variant",
              label: "Sample Variant",
              type: "select",
              options: [
                { value: "Normal", label: "Normal" },
                { value: "Organic", label: "Organic" },
                { value: "Loose", label: "Loose" },
              ],
            })}

          {/* Sample Type */}
          {fieldsBaseOnConfig?.sampleType?.required &&
            renderFormField({
              fieldKey: "sampleType",
              label: "Sample Type",
              type: "select",
              options: [
                { value: "Whole", label: "Whole" },
                { value: "Powder", label: "Powder" },
              ],
            })}

          {/* Destination Country */}
          {fieldsBaseOnConfig?.destinationCountry?.required &&
            renderFormField({
              fieldKey: "destinationCountry",
              label: "Destination Country",
              type: "select",
              options: DESTINATION_COUNTRIES,
            })}

          {/* Sampling Type */}
          {fieldsBaseOnConfig?.samplingType?.required &&
            renderFormField({
              fieldKey: "samplingType",
              label: "Sampling Type",
              type: "select",
              options: [
                { value: "Composite Sampling", label: "Composite Sampling" },
                { value: "Code-wise Sampling", label: "Code-wise Sampling" },
              ],
            })}

          {/* Codewise Reason - Only show if Code-wise Sampling is selected */}
          {samplingType === "Code-wise Sampling" &&
            renderFormField({
              fieldKey: "codewiseReason",
              label: "Codewise Reason",
              type: "textarea",
            })}

          {/* Expected Consignment Readiness */}
          {fieldsBaseOnConfig?.expectedConsignmentReadiness?.required &&
            renderFormField({
              fieldKey: "expectedConsignmentReadiness",
              label: "Expected Consignment Readiness",
              type: "date",
            })}

          {/* Expected Shipment Date */}
          {fieldsBaseOnConfig?.expectedShipmentDate?.required &&
            renderFormField({
              fieldKey: "expectedShipmentDate",
              label: "Expected Shipment Date",
              type: "date",
            })}

          {/* Batch ID */}
          {fieldsBaseOnConfig?.batchId?.required &&
            renderFormField({
              fieldKey: "batchId",
              label: "Batch ID",
            })}

          {/* Supplier */}
          {fieldsBaseOnConfig?.supplier?.required &&
            renderFormField({
              fieldKey: "supplier",
              label: "Supplier",
            })}

          {/* Sector */}
          {fieldsBaseOnConfig?.sector?.required &&
            renderFormField({
              fieldKey: "sector",
              label: "Sector",
              type: "select",
              options: [
                { value: "government", label: "Government" },
                { value: "private", label: "Private" },
                { value: "cooperative", label: "Cooperative" },
                { value: "export", label: "Export" },
                { value: "import", label: "Import" },
                { value: "domestic", label: "Domestic" },
              ],
            })}

          {/* Quantity */}
          {fieldsBaseOnConfig?.quantity?.required && (
            <>
              {renderFormField({
                fieldKey: "quantity",
                label: "Lot Quantity",
                type: "input",
              })}
              {renderFormField({
                fieldKey: "quantityUnit",
                label: "Quantity Unit",
                type: "select",
                options: [
                  { value: "", label: "None" },
                  { value: "Kg", label: "Kg" },
                  { value: "MT", label: "MT" },
                ],
              })}
            </>
          )}

          {/* Package Select */}
          <Form.Item
            name="package"
            label="Package"
            rules={[
              { required: true, message: "Please select a package type" },
            ]}
          >
            <Select placeholder="Select package type">
              {packages.map((pkg) => (
                <Option key={pkg._id} value={pkg._id}>
                  <div>
                    <div>{pkg.name}</div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#8c8c8c",
                        lineHeight: "1.2",
                      }}
                    >
                      {pkg.company?.companyName &&
                        `Company: ${pkg.company.companyName}`}
                      {pkg.company?.companyName && pkg.commodity?.name && " | "}
                      {pkg.commodity?.name &&
                        `Commodity: ${pkg.commodity.name}`}
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
          {fieldsBaseOnConfig?.siteAddress?.required &&
            renderFormField({
              fieldKey: "siteAddress",
              label: "Site Address",
              type: "input",
            })}

          {fieldsBaseOnConfig?.notes?.required &&
            renderFormField({
              fieldKey: "notes",
              label: "Additional Notes",
              type: "textarea",
              span: 1,
            })}
        </div>
      </Form>
    </Modal>
  );
};

export default EditOrderDetails;
